/**
 * @file 讲义打印页
 * @description 侧栏「讲义打印」：当前校区、当前教师名下学员的打印申请。前三条模拟数据分别预览三套试卷。
 *
 * TODO: 列表与试卷接口未接入；打印预览使用本地 JSON，立即打印调用浏览器打印。
 *
 * @whenToUse 路由 `/handouts`。
 * @whenNotToUse 其他业务菜单页。
 */

import { useState } from 'react'
import { Badge, type BadgeVariant } from '@/components/ui/badge.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { type DataTableColumn } from '@/components/ui/data-table.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import { RequestTable } from '@/components/ui/request-table.tsx'
import { toast } from '@/components/ui/toast.tsx'
import { HandoutPrintPreview } from '@/components/pages/handout-page/print/print-preview.tsx'
import {
  educationStageMap,
  gradeMap,
  learningLevelMap,
  semesterMap,
} from '@/enum/common.ts'
import { HandoutSearchFields } from '@/components/pages/handout-page/handout-search-fields.tsx'
import {
  readPrintAnswers,
  writePrintAnswers,
} from '@/components/pages/handout-page/print-answers-preference.ts'
import {
  EMPTY_HANDOUT_QUERY,
  HANDOUT_STATUS_LABEL,
  MOCK_HANDOUT_ROWS,
  filterHandoutRows,
  type HandoutPrintRow,
  type HandoutPrintStatus,
} from '@/components/pages/handout-page/types.ts'

const STATUS_BADGE: Record<HandoutPrintStatus, BadgeVariant> = {
  pending: 'warning',
  printed: 'success',
}

export function HandoutPage() {
  const [rows, setRows] = useState(MOCK_HANDOUT_ROWS)
  const [previewRow, setPreviewRow] = useState<HandoutPrintRow | null>(null)
  const [printAnswers, setPrintAnswers] = useState(readPrintAnswers)

  /**
   * 有试卷数据时打开预览；分页完成后由预览里的「立即打印」拉起系统打印。
   * @param row 打印申请行
   */
  const openPreview = (row: HandoutPrintRow) => {
    if (!row.paperKind) {
      toast.info('暂无试卷数据')
      return
    }
    setPreviewRow(row)
  }

  /** 系统打印已拉起，把该申请标为已打印 */
  const markPrinted = () => {
    if (!previewRow) return
    const printedId = previewRow.id
    setRows((current) =>
      current.map((item) =>
        item.id === printedId ? { ...item, status: 'printed' } : item,
      ),
    )
    toast.success('已发送到系统打印', previewRow.studentName)
  }

  const columns: DataTableColumn<HandoutPrintRow>[] = [
    {
      key: 'studentName',
      title: '学员名称',
      dataIndex: 'studentName',
      minWidth: 100,
      fixed: 'left',
    },
    {
      key: 'textbook',
      title: '教材',
      dataIndex: 'textbookName',
      minWidth: 180,
    },
    {
      key: 'grade',
      title: '年级',
      minWidth: 88,
      render: (row) => gradeMap[row.grade],
    },
    {
      key: 'semester',
      title: '学期',
      minWidth: 72,
      render: (row) => semesterMap[row.semester],
    },
    {
      key: 'stage',
      title: '阶段',
      minWidth: 72,
      render: (row) => educationStageMap[row.educationStage],
    },
    {
      key: 'learningLevel',
      title: '学习力等级',
      minWidth: 96,
      render: (row) => learningLevelMap[row.learningGrade],
    },
    {
      key: 'learningPower',
      title: '学习力',
      dataIndex: 'learningForce',
      minWidth: 72,
    },
    {
      key: 'teacherName',
      title: '所属教师',
      dataIndex: 'teacherName',
      minWidth: 96,
    },
    {
      key: 'status',
      title: '状态',
      minWidth: 88,
      render: (row) => (
        <Badge variant={STATUS_BADGE[row.status]}>
          {HANDOUT_STATUS_LABEL[row.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      title: '操作',
      fixed: 'right',
      width: 80,
      render: (row) =>
        <button
          type="button"
          className="text-primary transition-opacity duration-150 hover:opacity-90"
          onClick={() => openPreview(row)}
        >
          打印
        </button>
    },
  ]

  return (
    <>
      <PageHeader
        title="讲义打印"
        description="仅展示当前校区、当前教师名下学员提交的打印申请。"
      />
      <div className="mt-4">
        <RequestTable
          data={rows}
          filterData={filterHandoutRows}
          columns={columns}
          searchForm={{
            initialValues: EMPTY_HANDOUT_QUERY,
            items: HandoutSearchFields,
            extra: (
              <Checkbox
                className="h-9"
                label="是否打印答案"
                checked={printAnswers}
                onChange={(event) => {
                  const next = event.currentTarget.checked
                  setPrintAnswers(next)
                  writePrintAnswers(next)
                }}
              />
            ),
          }}
        />
      </div>
      <HandoutPrintPreview
        open={previewRow !== null}
        row={previewRow}
        onClose={() => setPreviewRow(null)}
        onPrinted={markPrinted}
        printAnswers={printAnswers}
      />
    </>
  )
}
